import React from "react";
import styled from "styled-components";
import Page from "../../components/Page";
import Async from "../../components/Async";
import { Button, ColoredButton } from "../../components/buttons";
import api from "../../services/api";
import toasts from "../../services/toasts";

const Card = styled.div`
  background: var(--button-bg);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  box-shadow: var(--button-shadow);
  padding: 18px;
`;

const CardTitle = styled.h4`
  margin: 0 0 14px;
`;

const Note = styled.p`
  margin: 0 0 16px;
  opacity: 0.75;
  line-height: 1.45;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 600;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--app-bg);
  color: var(--text-color);
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--app-bg);
  color: var(--text-color);
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const SummaryGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  margin-bottom: 18px;
`;

const SummaryItem = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 10px 12px;
  background: var(--app-bg);
`;

const SourceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SourceItem = styled.div`
  border: 1px solid var(--border-color);
  border-radius: 10px;
  padding: 14px;
  background: var(--app-bg);
`;

const SourceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 8px;
`;

const SourceTitle = styled.div`
  font-weight: 700;
`;

const Meta = styled.div`
  font-size: 13px;
  opacity: 0.75;
  line-height: 1.5;
`;

const SplitRow = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(220px, 1fr) minmax(240px, 1fr) auto;
  align-items: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const EmptyState = styled.div`
  padding: 24px 0;
  opacity: 0.75;
`;

const loadCurzonMappings = () => api.showtimes.curzon.listMappings();

const getErrorMessage = error => {
  if (error && error.response && error.response.data) {
    return error.response.data.message || error.response.data.error || error.toString();
  }
  return error.toString();
};

const formatDateTime = value => {
  if (!value) {
    return "Not imported yet";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
};

const formatDate = value => {
  if (!value) {
    return "Unknown";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString();
};

class Showtimes extends React.Component {
  state = {
    reload: false,
    savingCurzonSlug: null,
    importingCurzonSnapshot: false,
    curzonSelections: {}
  };

  refresh = () => {
    this.setState(state => ({ reload: !state.reload }));
  };

  setCurzonSelection = (slug, placeId) => {
    this.setState(state => ({
      curzonSelections: {
        ...state.curzonSelections,
        [slug]: placeId
      }
    }));
  };

  saveCurzonMapping = async (slug, fallbackPlaceId = "") => {
    const selectedValue = this.state.curzonSelections[slug] || fallbackPlaceId || "";
    if (!selectedValue) {
      toasts.error("Choose the Playground cinema that matches this Curzon venue.");
      return;
    }

    this.setState({ savingCurzonSlug: slug });
    try {
      const result = await api.showtimes.curzon.saveMapping({
        slug,
        place_id: Number(selectedValue),
        is_active: true
      });
      this.setState(state => ({
        curzonSelections: {
          ...state.curzonSelections,
          [slug]: String(result.place_id)
        }
      }));
      toasts.success(`Mapped ${result.venue_name} to ${result.place_name}.`);
      this.refresh();
    } catch (error) {
      toasts.error(getErrorMessage(error));
    } finally {
      this.setState({ savingCurzonSlug: null });
    }
  };

  importCurzonSnapshot = async () => {
    this.setState({ importingCurzonSnapshot: true });
    try {
      const result = await api.showtimes.curzon.importSnapshot({});
      toasts.success(
        `Imported ${result.totals.saved_showtimes} Curzon showtimes across ${result.totals.venues} venues.`
      );
      this.refresh();
    } catch (error) {
      toasts.error(getErrorMessage(error));
    } finally {
      this.setState({ importingCurzonSnapshot: false });
    }
  };

  renderMappings = payload => {
    const { curzonSelections, savingCurzonSlug, importingCurzonSnapshot } = this.state;
    const venues = (payload && payload.venues) || [];
    const candidates = (payload && payload.candidates) || [];
    const statusSummary = (payload && payload.status_summary) || {};

    if (!venues.length) {
      return <EmptyState>No Curzon venues are configured yet.</EmptyState>;
    }

    return (
      <Card>
        <CardTitle>Curzon Matching</CardTitle>
        <Note>
          Match each Curzon venue to the correct Playground cinema, then import the
          current Curzon snapshot into the database.
        </Note>

        <SummaryGrid>
          <SummaryItem>
            <strong>Mapped Venues</strong>
            <Meta>{statusSummary.mapped_venue_count || 0} of {venues.length}</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Imported Venues</strong>
            <Meta>{statusSummary.imported_venue_count || 0} with stored showtimes</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Upcoming Showtimes</strong>
            <Meta>{statusSummary.total_upcoming_showtime_count || 0} rows in DB</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Coverage Through</strong>
            <Meta>{formatDate(statusSummary.last_show_date)}</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Last Import</strong>
            <Meta>{formatDateTime(statusSummary.last_imported_at)}</Meta>
          </SummaryItem>
          <SummaryItem>
            <strong>Snapshot Fetched</strong>
            <Meta>{formatDateTime(payload && payload.snapshot_fetched_at)}</Meta>
          </SummaryItem>
        </SummaryGrid>

        <ButtonRow>
          <ColoredButton
            type="button"
            onClick={this.importCurzonSnapshot}
            disabled={importingCurzonSnapshot}
          >
            {importingCurzonSnapshot ? "Importing snapshot..." : "Import Curzon snapshot now"}
          </ColoredButton>
        </ButtonRow>

        <SourceList>
          {venues.map(venue => {
            const selectedValue =
              curzonSelections[venue.slug] !== undefined
                ? curzonSelections[venue.slug]
                : venue.mapped_place_id
                  ? String(venue.mapped_place_id)
                  : "";
            const selectedCandidate = candidates.find(
              candidate => String(candidate.place_id) === String(selectedValue)
            );
            const addressValue =
              (selectedCandidate && selectedCandidate.address) ||
              venue.mapped_place_address ||
              "";
            const curzonStatus = venue.curzon_status || null;

            return (
              <SourceItem key={venue.slug}>
                <SourceHeader>
                  <div>
                    <SourceTitle>{venue.venue_name}</SourceTitle>
                    <Meta>{venue.site_id} - {venue.query}</Meta>
                    {venue.mapped_place_name ? (
                      <Meta>
                        Currently mapped to {venue.mapped_place_name}
                        {venue.mapped_place_id ? ` (#${venue.mapped_place_id})` : ""}
                      </Meta>
                    ) : (
                      <Meta>No place selected yet.</Meta>
                    )}
                    {curzonStatus && curzonStatus.has_imported_showtimes ? (
                      <React.Fragment>
                        <Meta>
                          {curzonStatus.upcoming_showtime_count || 0} upcoming showtimes,
                          {" "}{curzonStatus.movie_count || 0} movies, {curzonStatus.stored_showtime_count || 0} stored total
                        </Meta>
                        <Meta>
                          Next show {formatDate(curzonStatus.next_show_date)}.
                          {" "}Coverage through {formatDate(curzonStatus.last_show_date)}.
                        </Meta>
                        <Meta>Last imported {formatDateTime(curzonStatus.last_imported_at)}</Meta>
                      </React.Fragment>
                    ) : venue.mapped_place_name ? (
                      <Meta>No Curzon showtimes imported for this mapped place yet.</Meta>
                    ) : null}
                  </div>
                </SourceHeader>

                <SplitRow>
                  <div>
                    <Label>Playground place</Label>
                    <Select
                      value={selectedValue}
                      onChange={event =>
                        this.setCurzonSelection(venue.slug, event.target.value)
                      }
                    >
                      <option value="">Choose a cinema place</option>
                      {candidates.map(candidate => {
                        const mappedElsewhere =
                          candidate.curzon_mapping_slug &&
                          candidate.curzon_mapping_slug !== venue.slug;
                        const label = `${candidate.place_name} (#${candidate.place_id})${
                          mappedElsewhere
                            ? ` - already mapped to ${candidate.curzon_mapping_slug}`
                            : ""
                        }`;

                        return (
                          <option
                            key={candidate.place_id}
                            value={candidate.place_id}
                            disabled={mappedElsewhere}
                          >
                            {label}
                          </option>
                        );
                      })}
                    </Select>
                  </div>

                  <div>
                    <Label>Address</Label>
                    <Input
                      value={addressValue}
                      placeholder="Select a cinema place to preview its address"
                      readOnly
                    />
                  </div>

                  <Button
                    type="button"
                    onClick={() => this.saveCurzonMapping(venue.slug, selectedValue)}
                    disabled={!selectedValue || savingCurzonSlug === venue.slug}
                  >
                    {savingCurzonSlug === venue.slug ? "Saving..." : "Save match"}
                  </Button>
                </SplitRow>
              </SourceItem>
            );
          })}
        </SourceList>
      </Card>
    );
  };

  render() {
    const { reload } = this.state;

    return (
      <Page title="Showtimes">
        <Async fetchProps={loadCurzonMappings} reload={reload}>
          {payload => this.renderMappings(payload)}
        </Async>
      </Page>
    );
  }
}

export default Showtimes;
